import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import Typography from '@mui/material/Typography'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import Box from '@mui/material/Box'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import Chip from '@mui/material/Chip'

import MDEditor from '@uiw/react-md-editor'

import { TerminologyModalStyle } from '../styles'
import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import ViewSourcesButton from '../shared/ViewSourcesButton'

import useFavourite from '../../hooks/useFavourite'
import { copyResourceLink } from './copyResourceLink'
import CiteTermModal from './CiteTermModal'

const TerminologyViewModal = (props) => {
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    const [open, setOpen] = useState(props.initialOpen || false)
    const { favourited, toggleFavourite } = useFavourite(props, 'terminology')

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
        if (props.onExternalClose) props.onExternalClose()
    }

    const sources = props.sources || []

    const handleCopyLink = async () => {
        await copyResourceLink({ dispatch, resourcePath: 'terminology', resourceId: props.id })
    }

    return (
        <div>
            {!props.initialOpen && <Button {...TerminologyModalStyle.viewButton.props} sx={TerminologyModalStyle.viewButton.style} onClick={()=>handleClickOpen()}>
                More Info
            </Button>}
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby={`terminology-modal-${props.id}`}
                open={open}
                fullWidth
                {...TerminologyModalStyle.modal}
            >
                <BootstrapDialogTitle id={`terminology-modal-${props.id}`} onClose={()=>handleClose()}>
                    <Box sx={TerminologyModalStyle.headingContainer}>
                        <MenuBookIcon sx={TerminologyModalStyle.icon}/>
                        {props.term}
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Box sx={TerminologyModalStyle.content}>
                        <Box sx={TerminologyModalStyle.dateContainer}>
                            <Box sx={TerminologyModalStyle.dates}>
                                <Typography {...TerminologyModalStyle.heading}>
                                    Updated:&nbsp;
                                </Typography>
                                <Typography {...TerminologyModalStyle.text}>
                                    {props.updated.split('T')[0]}
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Box sx={TerminologyModalStyle.ratingContainer}>
                                {user && !user.anon && <IconButton onClick={toggleFavourite}>
                                    {favourited? <BookmarkIcon sx={TerminologyModalStyle.favColour} /> : <BookmarkBorderIcon sx={TerminologyModalStyle.favColour} />}
                                </IconButton>}
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={TerminologyModalStyle.mobileContainer}>
                        <Box sx={TerminologyModalStyle.mobileRating}>
                            {user && !user.anon && <IconButton onClick={toggleFavourite}>
                                {favourited? <BookmarkIcon sx={TerminologyModalStyle.favColour} /> : <BookmarkBorderIcon sx={TerminologyModalStyle.favColour} />}
                            </IconButton>}
                        </Box>
                        <Box sx={TerminologyModalStyle.mobileDate}>
                            <Typography gutterBottom {...TerminologyModalStyle.dateHeading}>
                                Updated:&nbsp;
                            </Typography>
                            <Typography gutterBottom {...TerminologyModalStyle.text}>
                                {props.updated.split('T')[0]}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={TerminologyModalStyle.additionalInfo}>
                        <Typography {...TerminologyModalStyle.heading} gutterBottom>
                            Definition
                        </Typography>
                        <Box sx={{ marginTop: '10px' }}>
                            <MDEditor.Markdown
                                source={props.description}
                                style={{
                                    backgroundColor: 'transparent',
                                    color: 'inherit'
                                }}
                            />
                        </Box>
                    </Box>
                    {sources.length > 0 && (
                        <Box sx={TerminologyModalStyle.additionalInfo}>
                            <Typography {...TerminologyModalStyle.heading} gutterBottom>
                                {sources.length > 1 ? 'Sources' : 'Source'}
                            </Typography>
                            {sources.map((src, index) => (
                                <Box
                                    key={src.id ?? index}
                                    sx={{
                                        mt: index === 0 ? 0 : 2,
                                        pl: sources.length > 1 ? 2 : 0,
                                        borderLeft: sources.length > 1 ? '2px solid' : 'none',
                                        borderColor: 'divider',
                                    }}
                                >
                                    {src.source && (
                                        <Typography {...TerminologyModalStyle.text}>
                                            {src.source}
                                        </Typography>
                                    )}
                                    {src.publication_data && (
                                        <Typography {...TerminologyModalStyle.text} sx={{ mt: 0.5 }}>
                                            {src.publication_data}
                                        </Typography>
                                    )}
                                    {src.pub_date && (
                                        <Typography {...TerminologyModalStyle.text} sx={{ mt: 0.5 }}>
                                            Published: {src.pub_date}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                    {(props.tags.length > 0) &&
                    <><Box sx={TerminologyModalStyle.additionalInfo}>
                        <Typography {...TerminologyModalStyle.heading} gutterBottom>
                            Key Words
                        </Typography>
                    </Box>
                    <Box>
                        {props.tags && props.tags.map(tag => <Chip key={tag} label={tag} sx={TerminologyModalStyle.tags}/>)}
                    </Box></>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCopyLink}>
                        Copy Link&nbsp;<ContentCopyIcon />
                    </Button>
                    <CiteTermModal id={props.id} term={props.term} updated={props.updated} />
                    <ViewSourcesButton sources={sources} fontSize="medium" />
                    <Button autoFocus onClick={()=>handleClose()}>
                        Close
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default TerminologyViewModal
